<template>
  <div
    class="teaser object"
    :class="{ visible: ivisible, interactive }"
    :style="computedInlineStyles"
    tabindex="0"
    @animationend="handleAnimationEnd">
      <div class="image" :style="`background-image: url(${image});`"></div>
      <img ref="image" :src="image" @load="handleLoad" alt="">
      <div class="overlay">
        <h3>{{ title }}</h3>
        <p v-html="short" />
        <div>
          <CTA v-if="hasContent" :to="url">mehr erfahren</CTA>
        </div>
      </div>
      <em/><em/>
  </div>
</template>

<script>
  import CTA from '@/components/ui/CTA';

  export default {
    name: 'TeaserObject',
    components: {
      CTA,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      item: {
        type: Object,
        default: null,
      },
      stagger: {
        type: Number,
        default: 1,
      },
      visible: {
        type: Boolean,
        default: false,
      },
      inlineStyles: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        ivisible: false,
        interactive: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      date() {
        const item = this.item;
        if (item?.date) {
          return new Date(item.date);
        }
        return null;
      },
      title() {
        const item = this.item;
        if (item?.title) {
          return item.title;
        }
        return null;
      },
      short() {
        const item = this.item;
        if (item?.short) {
          return item.short;
        }
        return null;
      },
      image() {
        const item = this.item;
        if (item?.featured) {
          return `${this.$b.contentUrl}/${item.featured[0]?.full || item.featured[0]?.path}`;
        }
        return null;
      },
      url() {
        const item = this.item;
        if (item?.slug) {
          return `/objects/${item.slug}/`;
        }
        return null;
      },
      hasContent() {
        const item = this.item;
        return (item?.medias && item.medias.length > 0) || (item?.blocks && item.blocks.length > 0);
      },
      computedInlineStyles() {
        const stagger = this.stagger;
        const inlineStyles = this.inlineStyles;
        const style = `--i: ${stagger};`;
        if (inlineStyles) {
          return `${inlineStyles};${style}`;
        }
        return style;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      visible(val) {
        this.ivisible = val;
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleLoad() {
        if (!this.$listeners?.loaded) {
          this.ivisible = true;
        }
        this.$emit('loaded');
      },
      handleAnimationEnd() {
        this.interactive = true;
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.teaser.object {
  position: relative;
  overflow: hidden;
  pointer-events: none;
  outline: 0;

  &:after {
    position: absolute;
    top: -20px;
    left: -20px;
    content: '';
    width: calc(100% + 40px);
    height: calc(100% + 20px);
    pointer-events: none;
    box-shadow: inset 0 -2px 12px rgba(0,0,0,0);
    transition: box-shadow 0.45s linear;
  }

  & > .image {
    top: 0;
    left: 0;
    position: absolute;
    background-position: 50% 50%;
    background-size: cover;
    background-repeat: no-repeat;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity $default-transition;
  }

  & > img {
    visibility: hidden;
    width: 100%;
    height: auto;
  }

  & > em {
    position: absolute;
    pointer-events: none;
    width: 50px;
    height: 50px;
    border: 1px solid black;
    opacity: 0;

    &:first-of-type {
      bottom: 0;
      left: 0;
      border-top-width: 0;
      border-right-width: 0;
    }
    &:last-of-type {
      top: 0;
      right: 0;
      border-left-width: 0;
      border-bottom-width: 0;
    }
  }

  & > .overlay {
    left: 50%;
    bottom: 0;
    transform: translate(-50%, 120%);
    position: absolute;
    width: calc(100% - 20px);
    background: rgba(255, 255, 255, .85);
    user-select: none;
    pointer-events: none;
    transition: transform 0.45s ease;
    display: flex;
    padding: 20px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: black;
    font-size: initial;

    & > h3 {
      padding: 0;
      margin: 0 0 10px;
    }

    & > p {
      padding: 0;
      margin: 0;
    }
  }

  &:hover, &:focus, &:focus-within {
    &:after {
      box-shadow: inset 0 -2px 12px rgba(0,0,0,1);
    }
    & > .overlay {
      transform: translate(-50%, 0);
      pointer-events: all;
    }
  }

  &.visible {
    & > em {
      animation: fade-in-out 0.85s forwards;
    }
    & > .image {
      animation-name: zoom-in;
      animation-duration: 0.5s;
      animation-delay: calc(100ms + (var(--i) * 100ms));
      animation-fill-mode: forwards;
    }
  }

  &.interactive {
    pointer-events: all;
  }
}
</style>
